import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ProductGroup } from '@app/core/models/client';
import { MessageClass } from '@app/core/models/message';
import { ContentProChatComponent } from '../content-pro-chat/content-pro-chat.component';
import { FocusAiPreviewComponent } from '../focus-ai-preview/focus-ai-preview.component';
import { FocusAiService } from '../../services/focus-ai.service';

@Component({
  selector: 'rn-content-pro-modal',
    standalone: true,
  imports: [
    ContentProChatComponent,
    FocusAiPreviewComponent,
  ],
  templateUrl: './content-pro-modal.component.html',
  styleUrl: './content-pro-modal.component.scss',
})
export class ContentProModalComponent {
  @Input({ required: true }) messageId: string;
  @Input({ required: true }) emptyMessageContent: boolean;
  @Input({ required: true }) productGroup: ProductGroup;
  @Input({ required: true }) clientId: string;
  @Input({ required: true }) language: string;

  @Output() apply = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() contentProMessage = new EventEmitter<MessageClass>();
  @Output() contentProError = new EventEmitter<string>();

  @ViewChild(ContentProChatComponent) chatComponent: ContentProChatComponent;
  public focusAiService = inject(FocusAiService);

  generatedMessage: MessageClass | null = null;
  errorMessage: string = '';
  feedback: 'positive' | 'negative' | null = null;
  isVisible: boolean = false;
  showConfirmation: boolean = false;
  showDiscardConfirmation: boolean = false;

  showModal(): void {
    // Reset state when modal opens
    this.generatedMessage = null;
    this.errorMessage = '';
    this.feedback = null;
    this.showConfirmation = false;
    this.showDiscardConfirmation = false;
    this.isVisible = true;

    // Lock body scroll
    document.body.style.overflow = 'hidden';
  }

  onApplyClick(): void {
    // Show confirmation overlay
    this.showConfirmation = true;
  }

  onConfirmApply(): void {
    // Execute apply action
    this.isVisible = false;
    document.body.style.overflow = '';
    this.generatedMessage = null;
    this.errorMessage = '';
    this.feedback = null;
    this.showConfirmation = false;
    if (this.chatComponent) {
      this.chatComponent.resetChat();
    }
    this.apply.emit();
  }

  onCancelConfirmation(): void {
    // Hide confirmation overlay
    this.showConfirmation = false;
  }

  onCancel(): void {
    // Show discard confirmation if there's generated content
    if (this.generatedMessage) {
      this.showDiscardConfirmation = true;
    } else {
      this.confirmDiscard();
    }
  }

  onDiscardCancel(): void {
    // Hide discard confirmation and return to content pro
    this.showDiscardConfirmation = false;
  }

  confirmDiscard(): void {
    // Execute discard action
    this.isVisible = false;
    document.body.style.overflow = '';
    this.generatedMessage = null;
    this.errorMessage = '';
    this.feedback = null;
    this.showConfirmation = false;
    this.showDiscardConfirmation = false;
    if (this.chatComponent) {
      this.chatComponent.resetChat();
    }
    this.cancel.emit();
  }

  onContentProMessage(message: MessageClass): void {
    this.generatedMessage = message;
    this.errorMessage = '';
    this.contentProMessage.emit(message);
  }

  onContentProError(error: string): void {
    this.errorMessage = error;
    this.generatedMessage = null;
    this.contentProError.emit(error);
  }

  onFeedbackChange(feedback: 'positive' | 'negative' | null): void {
    this.feedback = feedback;
    if (!this.messageId) {
      return;
    }
    this.focusAiService
      .sendFeedBack(this.clientId, this.messageId, feedback)
      .subscribe({
        error: (error) => {
          console.error('Error sending feedback:', error);
        },
      });
  }
}
